import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WorkspaceButton, WorkspaceDialog, WorkspaceNotice } from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  SpreadsheetImportSheet,
  type ImportTarget,
} from '@/components/requirement-import/spreadsheet-import-sheet';
import type { ModuleOption } from '@/components/requirement-import/module-select';
import { engagementRequirementKeys } from '@/lib/api/engagement-requirements';
import {
  EngagementImportResultSchema,
  confirmEngagementImport,
  confirmedResultFromError,
  previewEngagementImport,
  uploadEngagementImport,
  useEngagementImportsQuery,
  type EngagementImportResult,
  type ImportHistoryItem,
} from '@/lib/api/requirement-spreadsheets';
import { formatDate, importResultMessage, plural } from './requirements-logic';

/**
 * Spreadsheet import into an engagement, and its history (T-0153;
 * E11-F06). The review surface is the shared `SpreadsheetImportSheet`;
 * this file supplies the engagement's requests and wording.
 */

export interface EngagementImportSheetProps {
  engagementId: string;
  /** "Client · Engagement", for the sheet description. */
  engagementLabel: string | null;
  /** Active library modules, for module names the file spells differently. */
  modules: ReadonlyArray<ModuleOption>;
  onClose: () => void;
  /** One file was imported and the table has been refetched. */
  onImported: (result: EngagementImportResult) => void;
}

export function EngagementImportSheet({
  engagementId,
  engagementLabel,
  modules,
  onClose,
  onImported,
}: EngagementImportSheetProps) {
  const qc = useQueryClient();
  const target = React.useMemo<ImportTarget<EngagementImportResult>>(
    () => ({
      variant: 'engagement',
      title: 'Import requirements',
      description: `${engagementLabel ? `Into ${engagementLabel}. ` : ''}Nothing is saved until you confirm.`,
      modules,
      upload: (file, signal, progress) =>
        uploadEngagementImport(engagementId, file, signal, progress),
      preview: (token, overrides, signal) =>
        previewEngagementImport(engagementId, token, overrides, signal),
      confirm: async (input) => {
        let result: EngagementImportResult;
        try {
          result = await confirmEngagementImport(engagementId, input);
        } catch (err) {
          // Confirmed already (a retry after a lost response): that result stands.
          const earlier = confirmedResultFromError(err, EngagementImportResultSchema);
          if (!earlier) throw err;
          result = earlier;
        }
        // Wait for the refetch so the caller lands on the filled table.
        await qc.invalidateQueries({ queryKey: engagementRequirementKeys.all(engagementId) });
        return result;
      },
      confirmLabel: (count) =>
        count > 0 ? `Import ${plural(count, 'requirement')}` : 'Nothing to import',
      describeResult: importResultMessage,
    }),
    [engagementId, engagementLabel, modules, qc],
  );
  return <SpreadsheetImportSheet target={target} onClose={onClose} onImported={onImported} />;
}

export interface ImportHistoryDialogProps {
  engagementId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The load being undone, if any. */
  undoingId: string | null;
  onUndo: (item: ImportHistoryItem) => void;
}

/** Past spreadsheet imports: who, when, what came in, and Undo (Scenario 8). */
export function ImportHistoryDialog({
  engagementId,
  open,
  onOpenChange,
  undoingId,
  onUndo,
}: ImportHistoryDialogProps) {
  const query = useEngagementImportsQuery(engagementId, open);
  const items = query.data?.items ?? [];
  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Imports"
      description="Spreadsheets imported or matched to the library in this engagement, newest first."
      widthClassName="w-[720px]"
      actions={
        <WorkspaceButton size="md" onClick={() => onOpenChange(false)}>
          Close
        </WorkspaceButton>
      }
    >
      {query.error ? (
        <ErrorMessage error={query.error} />
      ) : !query.data ? (
        <div className="flex items-center gap-2 py-4 text-body-sm text-ink-500">
          <Spinner /> Loading the imports…
        </div>
      ) : items.length === 0 ? (
        <p className="py-4 text-body-sm text-ink-500">
          No spreadsheet has been imported into this engagement yet.
        </p>
      ) : (
        <>
          <ul
            aria-label="Imports"
            className="divide-y divide-ink-100 rounded-card border border-ink-100"
          >
            {items.map((item) => (
              <li key={item.load_id} className="flex items-start gap-3 px-3.5 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-mono text-meta font-medium text-ink-900">
                      {item.file_name}
                    </span>
                    {item.kind === 'library_match' ? (
                      <StatusChip tone="neutral" size="sm">
                        Matched to the library
                      </StatusChip>
                    ) : null}
                  </div>
                  <div className="mt-0.5 text-meta text-ink-500">
                    {formatDate(item.imported_at)}
                    {item.imported_by_name ? ` by ${item.imported_by_name}` : ''}
                  </div>
                  <div className="mt-1 text-body-sm text-ink-700">
                    {plural(item.added, 'requirement')} added
                    {item.duplicates_skipped > 0
                      ? `, ${plural(item.duplicates_skipped, 'duplicate')} skipped`
                      : ''}
                    {item.duplicates_kept > 0
                      ? `, ${plural(item.duplicates_kept, 'duplicate')} kept`
                      : ''}
                    {item.excluded > 0 ? `, ${item.excluded.toLocaleString()} excluded` : ''}.{' '}
                    {item.undone
                      ? 'None is active now.'
                      : `${item.active.toLocaleString()} active${
                          item.removed > 0 ? `, ${item.removed.toLocaleString()} removed` : ''
                        }${item.edited > 0 ? `, ${item.edited.toLocaleString()} edited since` : ''}.`}
                  </div>
                </div>
                {item.undone ? (
                  <span className="inline-flex h-[22px] flex-none items-center rounded-full bg-ink-100 px-2.5 text-meta font-medium text-ink-700">
                    Undone
                  </span>
                ) : (
                  <WorkspaceButton
                    aria-label={`Undo the import of ${item.file_name}`}
                    disabled={undoingId != null}
                    onClick={() => onUndo(item)}
                  >
                    {undoingId === item.load_id ? 'Undoing…' : 'Undo'}
                  </WorkspaceButton>
                )}
              </li>
            ))}
          </ul>
          <WorkspaceNotice>
            Undo removes the requirements an import added, as one recorded action. A requirement
            edited since the import is kept, and you are told which. Removed requirements can be
            restored from the Removed view.
          </WorkspaceNotice>
        </>
      )}
    </WorkspaceDialog>
  );
}
